import { Component } from "react"

/* Components */
import { Input } from "../../components"

/* Modal */
import Modal from "react-modal"

/* Redux */
import { connect } from "react-redux"

/* Translate */
import { t } from "../../translates/translate"

/* REST API */
import { get, create, edit } from "../../api/Currency"

/* Helpers */
import { utils } from "../../helpers"

/* Icons */
import { CloseIcon, CheckIcon } from "./CurrencyIcons"

/* Sweet alert */
import Swal from "../../helpers/alert"


/* EMPTY FORM STATE */
const EMPTY = {
    id: 0,
    type: "create",
    status: "ok",
    name: "",
    code: "",
    symbol: "",
    isAfter: false,
    chipRate: "1",
    waiting: false,
}

/* LENGTH OF AN ISO CURRENCY CODE */
const CODE_LENGTH = 3

/* CHIP COEFFICIENT LIMITS — THE NOMINAL OF A CHIP IS MULTIPLIED BY IT */
const RATE_MIN = 1
const RATE_MAX = 99999


/* Widget Currency Form */
class CurrencyForm extends Component {

    constructor() {
        super()
        this.state = { visible: false, ...EMPTY }
    }

    /* Open Action */
    open = ({ type = "create", id = 0 }) => {

        this.setState({ visible: true, ...EMPTY, type, id: parseInt(id) ? parseInt(id) : 0 }, () => {
            this.load()
        })
    }

    /* Close Action */
    close = () => {
        this.setState({ visible: false, ...EMPTY })
    }

    /* Load Action — only the edit form has something to read */
    load = () => {

        /* Fields */
        const { type, id } = this.state

        /* Check Type */
        if (type !== "edit") {
            return
        }

        /* Set Status */
        this.setState({ status: "loading" })

        /* Send to REST API */
        get({ id }).then((response) => {
            if (response.status === 200) {
                this.setState({
                    status: "ok",
                    name: utils.checkValue(response.data.name, ""),
                    code: utils.checkValue(response.data.code, ""),
                    symbol: utils.checkValue(response.data.symbol, ""),
                    isAfter: parseInt(response.data.isAfter) === 1,
                    chipRate: utils.checkValue(response.data.chipRate, "1"),
                })
            }
            else {
                this.setState({ status: "error" })
                Swal.fire(utils.notification(this.message(response), "error"))
                this.close()
            }
        }).catch(() => {
            this.setState({ status: "error" })
            Swal.fire(utils.notification(t("swal.generic"), "error"))
            this.close()
        })

    }

    /* Read The Error Message Of A Response */
    message = (response) => {

        if (response && response.data && response.data.message) {
            return response.data.message
        }

        if (response && response.status === 498) {
            return t("swal.invalidKey")
        }

        if (response && (response.status === 401 || response.status === 403)) {
            return t("swal.noAccess")
        }

        return t("swal.generic")
    }

    /* Save Action */
    save = () => {

        /* Fields */
        const { type, id, name, code, symbol, isAfter, chipRate } = this.state
        const { reload } = this.props

        /* Check Name */
        if (!name.trim()) {
            this._name.error(t("currency.error.emptyName"))
            return
        }

        /* Check Code */
        if (!code.trim()) {
            this._code.error(t("currency.error.emptyCode"))
            return
        }

        /* Check Code Length */
        if (code.trim().length !== CODE_LENGTH) {
            this._code.error(t("currency.error.codeLength"))
            return
        }

        /* Check Symbol */
        if (!symbol.trim()) {
            this._symbol.error(t("currency.error.emptySymbol"))
            return
        }

        /* Check Chip Coefficient */
        const rate = parseInt(chipRate)

        if (!chipRate || isNaN(rate) || rate < RATE_MIN || rate > RATE_MAX) {
            this._chipRate.error(t("currency.error.chipRate"))
            return
        }

        /* Start Waiting */
        this.setState({ waiting: true })

        /* Prepare Request */
        const fields = { name: name.trim(), code: code.trim().toUpperCase(), symbol: symbol.trim(), isAfter: isAfter ? "yes" : "no", chipRate: rate }
        const request = type === "edit" ? edit({ id, ...fields }) : create(fields)

        /* Send to REST API */
        request.then((response) => {
            if (response.status === 200) {
                Swal.fire(utils.notification(type === "edit" ? t("currency.savedSuccess") : t("currency.addedSuccess"), "success"))
                reload()
                this.close()
            }
            else {
                Swal.fire(utils.notification(this.message(response), "error"))
            }
        }).catch(() => {
            Swal.fire(utils.notification(t("swal.generic"), "error"))
        }).finally(() => {
            this.setState({ waiting: false })
        })

    }

    /* Draw Body */
    _body = () => {

        /* Fields */
        const { status, name, code, symbol, isAfter, chipRate } = this.state

        /* Check Status */
        if (status !== "ok") {
            return <div className="currency--modal-loading"><div className="currency--loading" /></div>
        }

        return (
            <div className="currency--modal-body">

                <Input ref={(ref) => this._name = ref} label={t("currency.name")} value={name} onChange={(event) => this.setState({ name: event.target.value })} />

                <div className="currency--fields">
                    <Input ref={(ref) => this._code = ref} label={t("currency.code")} value={code} maxLength={CODE_LENGTH} onChange={(event) => this.setState({ code: event.target.value.toUpperCase() })} />
                    <Input ref={(ref) => this._symbol = ref} label={t("currency.symbol")} value={symbol} onChange={(event) => this.setState({ symbol: event.target.value })} />
                </div>

                <Input ref={(ref) => this._chipRate = ref} label={t("currency.chipRate")} value={chipRate} onChange={(event) => this.setState({ chipRate: event.target.value })} />

                <div className="currency--hint">{t("currency.chipRateHint")}</div>

                <div onClick={() => this.setState({ isAfter: !isAfter })} className={`currency--switch ${isAfter ? "active" : ""}`}>
                    <div className="currency--switch-mark"><CheckIcon size={11} /></div>
                    <span>{t("currency.symbolBefore")}</span>
                </div>

            </div>
        )
    }

    /* Draw Footer */
    _footer = () => {

        /* Fields */
        const { status, waiting } = this.state

        /* Check Status */
        if (status !== "ok") {
            return null
        }

        return (
            <div className="currency--modal-footer">

                <div onClick={() => this.close()} className="currency--button ghost">{t("common.cancel")}</div>

                {waiting
                    ? <div className="currency--button primary"><div className="currency--loading small" /></div>
                    : <div onClick={() => this.save()} className="currency--button primary">{t("common.save")}</div>
                }

            </div>
        )
    }

    render = () => {

        /* Fields */
        const { visible, type } = this.state

        return (
            <Modal
                isOpen={visible}
                onRequestClose={() => this.close()}
                className="currency--modal"
                overlayClassName="currency--overlay"
                closeTimeoutMS={200}
            >

                <div className="currency--modal-header">

                    <div>
                        <h2>{type === "edit" ? t("currency.editTitle") : t("currency.addTitle")}</h2>
                        <p>{type === "edit" ? t("currency.editSubtitle") : t("currency.createSubtitle")}</p>
                    </div>

                    <div onClick={() => this.close()} className="currency--modal-close">
                        <CloseIcon />
                    </div>

                </div>

                {/* Draw Body */}
                {this._body()}

                {/* Draw Footer */}
                {this._footer()}

            </Modal>
        )
    }

}

const mapStateToProps = (state) => ({
    language: state.language,
})

export default connect(mapStateToProps, null, null, { forwardRef: true })(CurrencyForm)
